import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ErrorCode, type ApiError, type ApiErrorDetail } from '@app/shared';

export type ApiQueryError = FetchBaseQueryError | SerializedError | undefined;

/** Human-readable message for every stable `ApiError.code` the backend can return. */
const ERROR_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.VALIDATION_ERROR]: 'Проверьте правильность заполнения формы',
  [ErrorCode.BAD_REQUEST]: 'Некорректный запрос',
  [ErrorCode.UNAUTHORIZED]: 'Необходимо войти в систему',
  [ErrorCode.FORBIDDEN]: 'Недостаточно прав',
  [ErrorCode.NOT_FOUND]: 'Не найдено',
  [ErrorCode.CONFLICT]: 'Конфликт данных',
  [ErrorCode.VERSION_CONFLICT]: 'Документ был изменён в другом окне — откройте свежую версию',
  [ErrorCode.UNPROCESSABLE_ENTITY]: 'Невозможно обработать запрос',
  [ErrorCode.TOO_MANY_REQUESTS]: 'Слишком много попыток — попробуйте позже',
  [ErrorCode.INTERNAL_ERROR]: 'Внутренняя ошибка сервера',

  [ErrorCode.STORE_HEADER_REQUIRED]: 'Не выбран магазин',
  [ErrorCode.MEMBERSHIP_NOT_APPROVED]: 'Ваша заявка на подключение ещё не одобрена',
  [ErrorCode.FORBIDDEN_ROLE]: 'Недостаточно прав для этого действия',
  [ErrorCode.PASSWORD_CHANGE_REQUIRED]: 'Сначала смените пароль',
  [ErrorCode.LOGIN_TAKEN]: 'Такой логин уже занят',
  [ErrorCode.INVALID_CREDENTIALS]: 'Неверный логин или пароль',

  [ErrorCode.STORE_CODE_NOT_FOUND]: 'Магазин с таким кодом не найден',
  [ErrorCode.JOIN_PAUSED]: 'Магазин временно не принимает заявки',
  [ErrorCode.JOIN_ATTEMPTS_EXCEEDED]: 'Слишком много попыток — попробуйте позже',
  [ErrorCode.JOIN_REQUEST_PENDING]: 'Заявка уже отправлена и ожидает решения',
  [ErrorCode.JOIN_REAPPLY_TOO_SOON]: 'Повторная заявка возможна не раньше чем через 3 дня после отклонения',
  [ErrorCode.ALREADY_MEMBER]: 'Вы уже подключены к этому магазину',
  [ErrorCode.COUNTERPARTY_ALREADY_LINKED]: 'У этого контрагента уже есть привязанный аккаунт',

  [ErrorCode.CATEGORY_NAME_TAKEN]: 'Такое название уже есть, в том числе в архиве',
  [ErrorCode.CATEGORY_DEPTH_EXCEEDED]: 'Дерево категорий — ровно два уровня',
  [ErrorCode.CATEGORY_PCS_PER_BOX_LOCKED]: 'По категории есть проведённые документы — штук в коробке не изменить',
  [ErrorCode.CATEGORY_DELETE_FORBIDDEN]: 'Категории никогда не удаляются',
  [ErrorCode.CATEGORY_HAS_CHILDREN]: 'У группы есть подкатегории — сделать её товаром нельзя',
  [ErrorCode.CATEGORY_ARCHIVED]: 'В строке архивная категория — замените её перед проведением',

  [ErrorCode.COUNTERPARTY_HAS_DOCUMENTS]: 'У контрагента есть документы или платежи — доступно только архивирование',
  [ErrorCode.COUNTERPARTY_ARCHIVED]: 'Контрагент в архиве',
  [ErrorCode.MERGE_BOTH_HAVE_ACCOUNTS]: 'У обеих карточек есть аккаунт клиента — объединить их нельзя',
  [ErrorCode.MERGE_SAME]: 'Нельзя объединить карточку саму с собой',

  [ErrorCode.INVALID_STATUS_TRANSITION]: 'Действие недоступно для текущего статуса документа',
  [ErrorCode.DOCUMENT_INCOMPLETE]: 'Заполните обязательные поля документа',
  [ErrorCode.EMPTY_DOCUMENT]: 'Документ пустой — нечего сохранять',
  [ErrorCode.STATE_VERSION_MISMATCH]: 'Данные изменились — предпросмотр обновлён, проверьте и сохраните снова',
  [ErrorCode.RECALC_PENDING]: 'Категория ещё пересчитывается — попробуйте позже',
  [ErrorCode.LOCK_TIMEOUT]: 'Не удалось получить блокировку — попробуйте ещё раз',
  [ErrorCode.CONCURRENT_UPDATE]: 'Данные одновременно изменили в другом окне — попробуйте ещё раз',
  [ErrorCode.TRANSACTION_TIMEOUT]: 'Операция заняла слишком много времени и отменена — попробуйте ещё раз',
  [ErrorCode.INVARIANT_VIOLATION]: 'Внутренняя ошибка складского учёта',
  [ErrorCode.LINE_REFERENCED_BY_RETURN]: 'На строку ссылается возврат — её нельзя удалить или сменить в ней товар',

  [ErrorCode.ALLOCATION_EXCEEDS_PAYMENT]: 'Сумма разнесения превышает сумму платежа',
  [ErrorCode.ALLOCATION_EXCEEDS_DEBT]: 'Сумма разнесения превышает долг по документу',
  [ErrorCode.ALLOCATION_WRONG_DIRECTION]: 'Документ не той стороны для этого платежа',
  [ErrorCode.OFFSET_SIDES_MISMATCH]: 'Суммы сторон взаимозачёта не совпадают',
  [ErrorCode.PAYMENT_LINKED_TO_DOCUMENT]: 'Платёж уже привязан к документу',

  [ErrorCode.ORDER_REQUEST_ALREADY_CONVERTED]: 'По этой заявке уже создана продажа',

  [ErrorCode.ATTACHMENT_LIMIT_PER_DOCUMENT]: 'Достигнут лимит вложений на документ',
  [ErrorCode.ATTACHMENT_TOO_LARGE]: 'Файл слишком большой',
  [ErrorCode.ATTACHMENT_QUOTA_EXCEEDED]: 'Превышен лимит вложений на магазин',
  [ErrorCode.INVALID_FILE_TYPE]: 'Недопустимый тип файла',
};

const NETWORK_ERROR_MESSAGE = 'Нет соединения с сервером';
const DEFAULT_ERROR_MESSAGE = 'Произошла ошибка. Попробуйте ещё раз';

function isApiError(data: unknown): data is ApiError {
  return (
    typeof data === 'object' && data !== null && 'code' in data && typeof data.code === 'string'
  );
}

function isKnownErrorCode(code: string): code is ErrorCode {
  return code in ERROR_MESSAGES;
}

/** A transport-level failure (offline, DNS, timeout) — as opposed to an answer from the server. */
export function isNetworkError(error: ApiQueryError): boolean {
  return (
    error !== undefined &&
    'status' in error &&
    (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR')
  );
}

/** Extracts the server's `ApiError.code` from an RTK Query error, if there is one. */
export function getErrorCode(error: ApiQueryError): string | undefined {
  if (!error || !('status' in error)) return undefined;
  return isApiError(error.data) ? error.data.code : undefined;
}

/** Коды, чей текст уточняется подробностями ответа (номер документа и т.п.). */
function detailedMessage(code: ErrorCode, details: ApiErrorDetail[] | undefined): string | undefined {
  if (code === ErrorCode.ORDER_REQUEST_ALREADY_CONVERTED) {
    const number = details?.find((d) => d.field === 'document')?.constraint;
    return number ? `По этой заявке уже создана продажа №${number}` : undefined;
  }
  return undefined;
}

/** Структурные подробности отказа (`ApiError.details`): номера строк, номер документа и т.п. */
export function getErrorDetails(error: unknown): ApiErrorDetail[] | undefined {
  const err = error as ApiQueryError;
  if (!err || !('status' in err)) return undefined;
  return isApiError(err.data) ? err.data.details : undefined;
}

/**
 * Maps an RTK Query error to a message: a known error code → its text, otherwise the raw server
 * message, otherwise a generic fallback. Accepts `unknown` too (e.g. from a `.unwrap().catch()`
 * or `try/catch`), since that's what a caught rejection is typed as.
 */
export function translateApiError(error: unknown): string {
  const err = error as ApiQueryError;
  if (!err || !('status' in err)) return DEFAULT_ERROR_MESSAGE;
  if (isNetworkError(err)) return NETWORK_ERROR_MESSAGE;

  const code = getErrorCode(err);
  if (code && isKnownErrorCode(code)) return detailedMessage(code, getErrorDetails(err)) ?? ERROR_MESSAGES[code];
  if (isApiError(err.data) && err.data.message) return err.data.message;
  return DEFAULT_ERROR_MESSAGE;
}
